import { useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { ApiError } from '../../../../lib/api-client';
import { PageHead } from '../../components';
import {
  useCannedResponseMetricsByOperator,
  useCannedResponseMetricsOverview,
  useConductAlertConfig,
  useUpdateConductAlertConfig,
} from '../../hooks/use-canned-analytics';

import { ConductAlertEditor } from './ConductAlertEditor';

import type React from 'react';

type RpPeriod = '7d' | '30d' | '90d';

const PERIOD_OPTS: { value: RpPeriod; label: string; days: number }[] = [
  { value: '7d', label: '7 dias', days: 7 },
  { value: '30d', label: '30 dias', days: 30 },
  { value: '90d', label: '90 dias', days: 90 },
];

/**
 * Janela em datas ISO (yyyy-mm-dd), chave de query ESTÁVEL dentro do dia
 * (não usar `now` cru → loop de refetch). `periodTo` é o limite superior
 * EXCLUSIVO = amanhã, para que a atividade de HOJE entre na janela (o Engine
 * filtra `sentAt < periodTo`; um `periodTo` = hoje truncaria à meia-noite e
 * excluiria o dia corrente — inconsistente com a by-operator, que termina em
 * `now`). Mirror do `periodWindow` da Tela 6, corrigido para incluir hoje.
 */
function periodWindow(days: number): { periodFrom: string; periodTo: string } {
  const day = 24 * 60 * 60 * 1000;
  const now = new Date();
  const from = new Date(now.getTime() - days * day);
  const toExclusive = new Date(now.getTime() + day);
  return {
    periodFrom: from.toISOString().slice(0, 10),
    periodTo: toExclusive.toISOString().slice(0, 10),
  };
}

function pct(v: number): string {
  return `${v}%`;
}

function count(v: number): string {
  return v.toLocaleString('pt-BR');
}

/** Estado vazio honesto: o recurso não está disponível para este tenant/perfil. */
function EmptyUnavailable(): React.JSX.Element {
  return (
    <>
      <PageHead title="Respostas Prontas — uso e conduta" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>💬</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Recurso não disponível</h3>
        <p>
          As métricas de uso das respostas prontas dependem de uma capability opt-in. Fale com o
          administrador do tenant para habilitá-la.
        </p>
      </div>
    </>
  );
}

/**
 * Gate-and-split SERVER-AUTHORITATIVE (mirror `QuickRepliesPage`): a cap
 * `canned-response-analytics` é opt-in e o /auth/me NÃO filtra optInRequired —
 * chega `true` no FE para qualquer supervisor de produção. Por isso disparamos
 * a query REAL (a config, 1 linha barata) e só entregamos a tela quando o
 * SERVIDOR confirma (ausência de 403). Um 403 real cai no estado "indisponível"
 * silenciosamente (esta é página NOVA → o fallback é um empty state, não um
 * catálogo legado).
 */
export function RespostasProntasSupervisorPage(): React.JSX.Element {
  const hint = useCapability('canned-response-analytics');
  if (!hint) return <EmptyUnavailable />;
  return <RespostasProntasGate />;
}

function RespostasProntasGate(): React.JSX.Element {
  const configQ = useConductAlertConfig(true);
  if (
    configQ.isError &&
    configQ.error instanceof ApiError &&
    configQ.error.status === 403
  ) {
    return <EmptyUnavailable />;
  }
  return <RespostasProntasView />;
}

function RespostasProntasView(): React.JSX.Element {
  // Todos os hooks ANTES de qualquer early return (rules-of-hooks).
  const [period, setPeriod] = useState<RpPeriod>('30d');
  const days = PERIOD_OPTS.find((o) => o.value === period)?.days ?? 30;
  const w = periodWindow(days);

  const overviewQ = useCannedResponseMetricsOverview(w, true);
  const byOperatorQ = useCannedResponseMetricsByOperator(true);
  const configQ = useConductAlertConfig(true);
  const updateMut = useUpdateConductAlertConfig();

  const overview = overviewQ.data;
  const operators = byOperatorQ.data?.data ?? [];
  const config = configQ.data;

  const isError = overviewQ.isError || byOperatorQ.isError || configQ.isError;
  const isLoading = overviewQ.isLoading || byOperatorQ.isLoading || configQ.isLoading;

  // 409 If-Match → a config mudou entre carregar e salvar (outro supervisor).
  const saveConflict =
    updateMut.isError && updateMut.error instanceof ApiError && updateMut.error.status === 409;
  const saveOtherError = updateMut.isError && !saveConflict;

  const maxCategory = Math.max(1, ...(overview?.byCategory.map((c) => c.sentCount) ?? [0]));
  const overviewEmpty = overview?.totalOutbound === 0;

  return (
    <>
      <PageHead
        title="Respostas Prontas — uso e conduta"
        subtitle="Como a equipe usa as respostas prontas — sinal para a conversa de gestão, nunca punição."
        filters={
          isError ? undefined : (
            <div className="ti-filters">
              {PERIOD_OPTS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={'chip' + (period === opt.value ? ' chip--active' : '')}
                  onClick={() => {
                    setPeriod(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )
        }
      />

      {isError && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Falha ao carregar as métricas de respostas prontas.</span>
          </div>
        </div>
      )}

      {isLoading && !isError && (
        <div className="tela6-body">
          <div className="tweaks-hint">Carregando métricas…</div>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="tela6-body">
          {/* Bloco 1 — Panorama (overview). */}
          <section className="tela6-section">
            <h2>Panorama do período</h2>
            {overview === undefined || overviewEmpty ? (
              <div className="tweaks-hint">
                Nenhum uso de resposta pronta neste período.
              </div>
            ) : (
              <>
                <div className="quality-globals">
                  <div className="quality-globals__card">
                    <h3>% via resposta pronta</h3>
                    <div className="quality-globals__value">{pct(overview.cannedRatePct)}</div>
                    <div className="tweaks-hint" style={{ marginTop: 6 }}>
                      {count(overview.cannedCount)} de {count(overview.totalOutbound)} mensagens
                    </div>
                  </div>
                  <div className="quality-globals__card">
                    <h3>Taxa de edição</h3>
                    <div className="quality-globals__value">{pct(overview.editRatePct)}</div>
                    <div className="tweaks-hint" style={{ marginTop: 6 }}>
                      {count(overview.editedCount)} editadas antes de enviar
                    </div>
                  </div>
                  <div className="quality-globals__card">
                    <h3>Coladas sem edição</h3>
                    <div className="quality-globals__value">{pct(overview.pastedWithoutEditPct)}</div>
                    <div className="tweaks-hint" style={{ marginTop: 6 }}>
                      {count(overview.pastedWithoutEditCount)} enviadas sem alteração
                    </div>
                  </div>
                  <div className="quality-globals__card">
                    <h3>Total de mensagens do operador</h3>
                    <div className="quality-globals__value">{count(overview.totalOutbound)}</div>
                    <div className="tweaks-hint" style={{ marginTop: 6 }}>
                      saídas humanas no período
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--space-4)',
                    marginTop: 'var(--space-4)',
                  }}
                >
                  <div>
                    <h3 className="quality-sub-h">Mais usadas</h3>
                    {overview.topResponses.length === 0 ? (
                      <div className="tweaks-hint">Sem envios no período.</div>
                    ) : (
                      <ol className="quality-rank">
                        {overview.topResponses.map((r) => (
                          <li
                            key={r.cannedResponseId}
                            style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
                          >
                            <span>
                              {r.title}
                              {r.category !== null && (
                                <span className="tweaks-hint"> · {r.category}</span>
                              )}
                            </span>
                            <b>
                              {count(r.sentCount)}
                              <span className="tweaks-hint">
                                {' '}
                                ({r.sentCount > 0
                                  ? Math.round((r.editedCount / r.sentCount) * 100)
                                  : 0}
                                % editada)
                              </span>
                            </b>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                  <div>
                    <h3 className="quality-sub-h">Por categoria</h3>
                    {overview.byCategory.length === 0 ? (
                      <div className="tweaks-hint">Sem envios no período.</div>
                    ) : (
                      <div className="quality-globals__dist">
                        {overview.byCategory.map((c) => (
                          <div key={c.category} className="quality-globals__dist-row">
                            <span style={{ minWidth: 90, color: 'var(--fg-3)' }}>{c.category}</span>
                            <div className="quality-globals__dist-bar">
                              <div
                                className="quality-globals__dist-bar-fill"
                                style={{
                                  width: `${((c.sentCount / maxCategory) * 100).toString()}%`,
                                  background: 'var(--brand)',
                                }}
                              />
                            </div>
                            <b style={{ minWidth: 30, textAlign: 'right' }}>{count(c.sentCount)}</b>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Bloco 2 — Por operador. `alertFlagged` é SINAL (destaque + badge),
              NUNCA ação/bloqueio/punição. */}
          <section className="tela6-section">
            <h2>Por operador</h2>
            {config !== undefined && (
              <div className="tweaks-hint" style={{ marginBottom: 'var(--space-2)' }}>
                Janela do alerta: últimos {config.windowDays}{' '}
                {config.windowDays === 1 ? 'dia' : 'dias'} (independente do período do panorama
                acima).
              </div>
            )}
            {config !== undefined && !config.enabled && (
              <div className="tweaks-hint" style={{ marginBottom: 'var(--space-2)' }}>
                Alerta de conduta desligado — nenhuma linha é destacada.
              </div>
            )}
            {operators.length === 0 ? (
              <div className="tweaks-hint">Nenhum uso de resposta pronta na janela do alerta.</div>
            ) : (
              <table className="q-table">
                <thead>
                  <tr>
                    <th>Operador</th>
                    <th className="num">Total</th>
                    <th className="num">Via resp. pronta</th>
                    <th className="num">% edição</th>
                    <th className="num">% coladas s/ edição</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.map((o) => (
                    <tr key={o.operatorUserId} className={o.alertFlagged ? 'at-risk' : ''}>
                      <td>
                        <b>{o.operatorName}</b>
                        {o.alertFlagged && <span className="rp-flag-badge">atenção</span>}
                      </td>
                      <td className="num">{count(o.totalOutbound)}</td>
                      <td className="num">{count(o.cannedCount)}</td>
                      <td className="num">{pct(o.editRatePct)}</td>
                      <td className="num">{pct(o.pastedWithoutEditPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="tweaks-hint" style={{ marginTop: 'var(--space-2)' }}>
              A marca “atenção” é um sinal para a conversa de gestão — não bloqueia envio nem pune.
            </div>
          </section>

          {/* Bloco 3 — Editor do alerta de conduta (inline). */}
          <section className="tela6-section">
            <h2>Alerta de conduta</h2>
            {config === undefined ? (
              <div className="tweaks-hint">Carregando configuração…</div>
            ) : (
              <ConductAlertEditor
                key={config.version}
                config={config}
                saving={updateMut.isPending}
                conflict={saveConflict}
                otherError={saveOtherError}
                onSave={(body) => {
                  updateMut.mutate({ version: config.version, body });
                }}
              />
            )}
          </section>
        </div>
      )}
    </>
  );
}
